import { formatDistanceToNow } from 'date-fns';
import { useState, useEffect } from 'react';

// Custom timestamp formatter that handles edge cases properly
export const formatTimestamp = (timestamp) => {
  try {
    // Ensure we have a valid timestamp
    if (!timestamp) {
      return 'now';
    }
    
    let messageTime;
    
    // Handle both ISO string and millisecond timestamps
    if (typeof timestamp === 'string') {
      // ISO string format like "2025-10-17T13:39:45.956Z"
      messageTime = new Date(timestamp).getTime();
    } else {
      // Millisecond timestamp
      messageTime = parseInt(timestamp);
    }
    
    // Check if conversion failed
    if (isNaN(messageTime)) {
      console.warn('Invalid timestamp detected:', timestamp);
      return 'now';
    }
    
    const now = Date.now();
    
    // Sanity check: if timestamp is in the future or more than 10 years old, something's wrong
    if (messageTime > now + 60000 || messageTime < now - (10 * 365 * 24 * 60 * 60 * 1000)) {
      console.warn('Invalid timestamp range:', timestamp, 'got', messageTime);
      return 'now';
    }
    
    const timeDiff = now - messageTime;
    
    // Handle very recent messages (under 1 minute) explicitly - this is critical for the mobile bug fix
    if (timeDiff < 60000) {
      return 'just now';
    }
    
    // Use date-fns for everything else
    return formatDistanceToNow(new Date(messageTime), { addSuffix: true });
  } catch (error) {
    console.error('Error formatting timestamp:', error, timestamp);
    return 'now';
  }
};

// Presence "last online" formatter.
// Deliberately compact ("5m"/"2h"/"3d") and always prefixed with "last seen" so it
// reads clearly as a PRESENCE time and is never confused with the verbose
// last-message-received time ("5 minutes ago") rendered elsewhere.
export const formatLastSeen = (timestamp) => {
  if (!timestamp) return '';
  let t;
  if (typeof timestamp === 'string') {
    t = new Date(timestamp).getTime();
  } else {
    t = parseInt(timestamp);
  }
  if (isNaN(t)) return '';

  const diff = Date.now() - t;
  if (diff < 60000) return 'last seen just now';

  const min = Math.floor(diff / 60000);
  if (min < 60) return `last seen ${min}m ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `last seen ${hr}h ago`;

  const days = Math.floor(hr / 24);
  if (days < 7) return `last seen ${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `last seen ${weeks}w ago`;

  // Older than a month — show an absolute date instead of an ever-growing count.
  try {
    return `last seen ${new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  } catch (_) {
    return 'last seen a while ago';
  }
};

// Live-updating presence label. Ticks every 60s so "5m" rolls to "6m" without
// needing a new socket event.
export const useFormattedLastSeen = (timestamp) => {
  const [label, setLabel] = useState(() => formatLastSeen(timestamp));

  useEffect(() => {
    setLabel(formatLastSeen(timestamp));
    const interval = setInterval(() => {
      setLabel(formatLastSeen(timestamp));
    }, 60000);
    return () => clearInterval(interval);
  }, [timestamp]);

  return label;
};

// React hook for live updating timestamps
export const useFormattedTimestamp = (timestamp) => {
  const [formattedTime, setFormattedTime] = useState(() => formatTimestamp(timestamp));
  
  useEffect(() => {
    // Update immediately
    const newTime = formatTimestamp(timestamp);
    setFormattedTime(newTime);
    
    // Set up interval to update every 30 seconds for live updates
    const interval = setInterval(() => {
      const updatedTime = formatTimestamp(timestamp);
      setFormattedTime(updatedTime);
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, [timestamp]);
  
  return formattedTime;
};
